<template>
  <div class="payphone-box">
    <!-- Lanzador con imagen -->
    <v-btn :block="block" depressed color="transparent" class="pp-launcher" :disabled="loading" @click="open">
      <v-img :src="buttonImageUrl" contain :max-height="buttonMaxHeight" :max-width="buttonMaxWidth" class="mx-auto" />
    </v-btn>

    <!-- Popup -->
    <v-dialog v-model="dialog" :fullscreen="$vuetify.breakpoint.xsOnly" :max-width="maxWidth" scrollable
      :persistent="persistent" :overlay-color="'#0b1220'" :overlay-opacity="0.75" content-class="pp-dialog"
      @input="v => { if (!v) $emit('closed') }">
      <v-card class="pp-card">
        <!-- Header -->
        <div class="pp-header">
          <div class="pp-chip">
            <v-icon size="18" class="mr-1">mdi-shield-check</v-icon>
            Pago seguro
          </div>

          <!-- IP monitoreada a la derecha, manteniendo el estilo de chip -->
          <div v-if="clientIp" class="pp-ipchip">
            <v-icon size="16" class="mr-1">mdi-lan</v-icon>
            Tu IP: {{ clientIp }}
          </div>

          <h3 class="pp-title">{{ title }}</h3>
          <v-btn icon class="pp-close" @click="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <!-- Cuerpo -->
        <v-card-text class="pp-body">
          <!-- FORM: visible si showForm -->
          <v-form v-if="showForm" ref="form" v-model="valid" lazy-validation class="pp-form">
            <!-- MONTO DESTACADO -->
            <div class="pp-amount">
              <label class="pp-amount__label">Monto (USD)</label>
              <v-text-field v-model.number="amountUsd" type="number" min="0" step="0.01" prefix="$"
                :rules="[v => v !== '' || 'Requerido', v => Number(v) > 0 || 'Debe ser mayor a 0']" hide-details="auto"
                outlined dense class="pp-amount__input" />
              <small class="pp-amount__hint">
                Ingresa el monto que deseas recargar
              </small>
            </div>

            <!-- REFERENCIA -->
            <v-text-field v-model="reference" label="Referencia / Motivo"
              :rules="[v => !!v || 'Requerido', v => String(v).length <= 100 || 'Máx 100']" counter="100" outlined dense
              class="pp-input" />

            <!-- DATOS TITULAR -->
            <div class="pp-grid">
              <v-text-field v-model="email" label="Email del titular" type="email" outlined dense class="pp-input"
                :rules="[v => !!v || 'Requerido', v => /.+@.+\..+/.test(v) || 'Email inválido']"
                disabled />
              <vue-tel-input
                        @input="onInput"
                        :required="true"
                        :preferred-countries="['ve','us','pe']"
                        class="pp-input"
                        placeholder="Teléfono"
                        outlined dense
                        v-model="phoneNumber"
                        :rules="[v => !!v || 'Requerido']"
                      ></vue-tel-input>
              <!-- <v-text-field v-model="phoneNumber" label="Teléfono (+593 / +58 / +1 ...)" outlined dense class="pp-input"
                :rules="[v => !!v || 'Requerido']" /> -->
              <v-text-field v-model="documentId" label="Documento de identidad" outlined dense class="pp-input"
                :rules="[v => !!v || 'Requerido']" />
              <v-select v-model="identificationType" :items="idTypes" item-text="text" item-value="value"
                label="Tipo de identificación" outlined dense class="pp-input" />
            </div>

            <!-- CTA -->
            <v-btn class="pp-cta" :loading="loading" :disabled="!valid || loading" @click="renderBox">
              <v-icon left>mdi-lock</v-icon>
              Generar pago
            </v-btn>

            <p class="pp-help">
              Pagos cifrados. Nunca almacenamos tu tarjeta.
            </p>
            <small v-if="amountNumber > 0" class="pp-amount__hint pp-amount__hint--compact">
              <strong>Fee (10%):</strong> ${{ feeAmount.toFixed(2) }}
            </small>
            <small v-if="amountNumber > 0" class="pp-amount__hint pp-amount__hint--compact">
              <strong>Recibirás:</strong> ${{ netAmount.toFixed(2) }}
            </small>
          </v-form>

          <!-- Loading mientras se oculta el form -->
          <div v-if="loading && !showForm" class="pp-loading">
            <div class="pp-spinner"></div>
            Preparando métodos de pago…
          </div>

          <!-- Aquí Payphone renderiza su botón/box -->
          <div :id="boxTargetId" class="pp-box"></div>

          <!-- Botón para volver a editar -->
          <div v-if="!showForm && !loading" class="pp-edit">
            <v-btn text small class="pp-edit-btn" @click="editAgain">
              <v-icon left small>mdi-pencil</v-icon>
              Editar datos
            </v-btn>
          </div>
        </v-card-text>

        <!-- Footer -->
        <v-card-actions class="pp-actions">
          <v-spacer />
          <v-btn text class="pp-secondary" @click="close">Cerrar</v-btn>
        </v-card-actions>

        <!-- 🔒 GATE OVERLAY: bloquea todo cuando no está verificado -->
        <div v-if="showGate" class="pp-gate">
          <div class="pp-gate__box">
            <v-icon size="28" class="mr-2">mdi-shield-lock</v-icon>
            <div class="pp-gate__title">Debes estar verificado</div>
            <div class="pp-gate__msg">Debes estar verificado para usar este método de recarga.</div>
            <div class="pp-gate__actions">
              <v-btn text class="pp-gate__btn--secondary" @click="onGateClose">Cerrar</v-btn>
              <v-btn class="pp-gate__btn--primary" @click="onGateVerify">
                <v-icon left>mdi-account-check</v-icon>
                Verifícate ahora
              </v-btn>
            </div>
          </div>
        </div>
        <!-- /GATE -->
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: 'PayphoneBox',
  props: {
    payphoneToken: { type: String, default: () => process.env.VUE_APP_PAYPHONE_TOKEN || '' },
    payphoneStoreId: { type: String, default: () => process.env.VUE_APP_PAYPHONE_STOREID || '' },
    title: { type: String, default: 'Recargar con tarjeta' },
    block: { type: Boolean, default: true },
    maxWidth: { type: [Number, String], default: 560 },
    persistent: { type: Boolean, default: false },
    buttonImageUrl: { type: String, default: 'https://trackandraces.com/cuentas/tarjetatar.png' },
    buttonMaxWidth: { type: [Number, String], default: 220 },
    buttonMaxHeight: { type: [Number, String], default: 90 },
    defaultAmountUsd: { type: [Number, String], default: '' },
    defaultReference: { type: String, default: 'Recarga de saldo' },
    defaultEmail: { type: String, default: '' },
    defaultPhone: { type: String, default: '' },
    defaultDocumentId: { type: String, default: '' },
    defaultIdentificationType: { type: Number, default: 1 },

    /* 🔒 Estado de verificación (pásalo desde el padre) */
    isUserVerified: { type: Boolean, default: false },

    /* 🔗 Ruta para verificar */
    verifyRoute: { type: String, default: '/cuenta' },
  },
  data() {
    return {
      dialog: false,
      valid: false,
      loading: false,
      showForm: true,
      isVerified: false,
      isApproved: false,
      amountUsd: '',
      reference: '',
      email: '',
      idpos: '',
      phoneNumber: '',
      documentId: '',
      identificationType: 1,
      idTypes: [
        { text: 'Cédula', value: 1 },
        { text: 'RUC', value: 2 },
        { text: 'Pasaporte', value: 3 },
      ],
      ppbInstance: null,

      // IP + fee
      clientIp: '',
      feeRate: 0.10,

      // Gate
      blocked: false,
    };
  },
  computed: {
    boxTargetId() {
      return `pp-box-${this._uid}`;
    },
    amountNumber() {
      const n = Number.parseFloat(this.amountUsd);
      return Number.isFinite(n) && n > 0 ? n : 0;
    },
    feeAmount() {
      const v = this.amountNumber * this.feeRate;
      return Math.round((v + Number.EPSILON) * 100) / 100;
    },
    netAmount() {
      const v = this.amountNumber - this.feeAmount;
      return v > 0 ? Math.round((v + Number.EPSILON) * 100) / 100 : 0;
    },
    showGate() {
      return this.dialog && this.blocked;
    }
  },
  mounted() {
    this.checkVerificationStatus();
    this.amountUsd = this.defaultAmountUsd;
    this.reference = this.defaultReference;
    this.email = this.defaultEmail;
    this.phoneNumber = this.defaultPhone;
    this.documentId = this.defaultDocumentId;
    this.identificationType = this.defaultIdentificationType;
    this.idpos = this.$store.state.idpos;

    window.addEventListener('message', this.onPayphoneMessage);
    this.fetchClientIp(); // obtiene IP para el header
  },
  beforeDestroy() {
    window.removeEventListener('message', this.onPayphoneMessage);
  },
  methods: {

    async checkVerificationStatus() {
      try {
        this.loading = true;
        const response = await axios.get('/verification/status');
        const { verified, approved } = response.data;

        this.isVerified = verified;
        this.isApproved = approved;

      } catch (error) {
        console.error('Error checking verification status:', error);
        this.isVerified = false;
        this.isApproved = false;
      } finally {
        this.loading = false;
      }
    },
    async fetchClientIp() {
      try {
        const ctl = new AbortController();
        const t = setTimeout(() => ctl.abort(), 5000);
        const res = await fetch('https://api.ipify.org?format=json', { signal: ctl.signal });
        clearTimeout(t);
        if (!res.ok) throw new Error('No se pudo obtener IP');
        const { ip } = await res.json();
        this.clientIp = ip || '';
      } catch (_) {
        this.clientIp = '';
      }
    },
    open() {
      this.dialog = true;
      this.ensureAssets();
      if (this.isVerified && this.isApproved) {
        this.isUserVerified = true;
      }
      // Si NO está verificado, bloquea el modal con el gate
      if (!this.isUserVerified) {
        this.blocked = true;
      }
    },onInput(formattedNumber, { number }) {
      this.phoneNumber = number.international;
      //this.phoneNumber = number.significant;
    },
    onGateClose() {
      // Cierra el modal completo
      this.blocked = false;
      this.close();
    },
    onGateVerify() {
      // Navega a la ruta de verificación
      this.$router && this.$router.push(this.verifyRoute);
      // Cierra el modal
      this.blocked = false;
      this.close();
    },

    close() {
      this.dialog = false;
      this.resetBox();
      this.showForm = true;
    },
    editAgain() {
      this.resetBox();
      this.showForm = true;
    },
    resetBox() {
      const target = document.getElementById(this.boxTargetId);
      if (target) target.innerHTML = '';
      this.ppbInstance = null;
    },
    usdToCents(usd) {
      const n = Number(usd || 0);
      return Math.round(n * 100);
    },
    ensureAssets() {
      if (!document.querySelector('link[data-ppbox-css]')) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://cdn.payphonetodoesposible.com/box/v1.1/payphone-payment-box.css';
        link.setAttribute('data-ppbox-css', '1');
        document.head.appendChild(link);
      }
      if (!document.querySelector('script[data-ppbox-js]')) {
        const script = document.createElement('script');
        script.type = 'module';
        script.src = 'https://cdn.payphonetodoesposible.com/box/v1.1/payphone-payment-box.js';
        script.setAttribute('data-ppbox-js', '1');
        document.body.appendChild(script);
      }
    },
    async waitForSDK() {
      return new Promise((resolve, reject) => {
        let tries = 0;
        const t = setInterval(() => {
          if (typeof PPaymentButtonBox !== 'undefined') {
            clearInterval(t);
            resolve();
          }
          if (++tries > 50) {
            clearInterval(t);
            reject(new Error('Payphone SDK no cargó a tiempo'));
          }
        }, 100);
      });
    },
    originCode() {
      const o = window.location.origin;
      if (o === 'https://trackandraces.com') return 'a';
      if (o === 'https://tar.la') return 'b';
      if (o === 'https://trackandraces.net') return 'c';
      if (o === 'http://localhost:8080') return 'd';
      return 'a';
    },
    routeCode() {
      return 'H1';
    },
    buildClientTxId() {
      const ts = Date.now();
      const rnd = Math.random().toString(36).slice(2, 8);
      const code = `${this.originCode()}-${this.routeCode()}`;
      return `TRX-${ts}-${rnd}-rt:${code}`;
    },
    async renderBox() {
      const ok = await this.$refs.form.validate();
      if (!ok) return;
      if (!this.payphoneToken || !this.payphoneStoreId) {
        this.$emit('error', 'Faltan credenciales de Payphone (token/storeId).');
        this.$toast && this.$toast.error('Configura VUE_APP_PAYPHONE_TOKEN y VUE_APP_PAYPHONE_STOREID');
        return;
      }
      this.loading = true;
      try {
        await this.waitForSDK();
        this.resetBox();
        this.showForm = false;
        const amount = this.usdToCents(this.amountUsd);
        const amountWithoutTax = amount;
        const amountWithTax = 0;
        const tax = 0;
        const service = 0;
        const tip = 0;
        const clientTxId = this.buildClientTxId();
        // eslint-disable-next-line no-undef
        this.ppbInstance = new PPaymentButtonBox({
          token: this.payphoneToken,
          storeId: this.payphoneStoreId,
          clientTransactionId: clientTxId,
          amount,
          amountWithoutTax,
          amountWithTax,
          tax,
          service,
          tip,
          currency: 'USD',
          reference: this.reference,
          lang: 'es',
          defaultMethod: 'card',
          email: this.email || undefined,
          optionalParameter1: this.idpos || undefined,
          phoneNumber: this.phoneNumber || undefined,
          documentId: this.documentId || undefined,
          identificationType: this.identificationType,
        }).render(this.boxTargetId);
        this.$emit('ready', { clientTransactionId: clientTxId, amount });
      } catch (e) {
        console.error(e);
        this.$emit('error', e?.message || 'No fue posible generar el botón de Payphone.');
        this.$toast && this.$toast.error('No fue posible generar el botón de Payphone.');
        this.showForm = true;
      } finally {
        this.loading = false;
      }
    },
    onPayphoneMessage(e) {
      const msg = e.data || {};
      if (msg.type === 'PAYPHONE_DONE') {
        const payload = msg.payload || {};
        const statusCode =
          payload?.data?.statusCode ??
          payload?.statusCode ??
          payload?.data?.data?.statusCode ??
          null;
        if (statusCode === 3) {
          this.$toast && this.$toast.success('Pago aprobado');
          this.$emit('approved', payload);
          this.$emit('success', payload);
        } else if (statusCode === 2) {
          this.$toast && this.$toast.error('Pago cancelado');
          this.$emit('declined', payload);
          this.$emit('error', 'Pago cancelado');
        } else {
          this.$toast && this.$toast.error('No se pudo confirmar el pago');
          this.$emit('error', 'No se pudo confirmar el pago');
        }
        this.close();
      } else if (msg.type === 'PAYPHONE_ERROR') {
        this.$toast && this.$toast.error('Error en confirmación');
        this.$emit('error', msg.payload || 'Error en confirmación');
        this.close();
      }
    },
  },
};
</script>

<style scoped>
/* ===== Paleta clara minimalista ===== */
:root {
  --pp-bg: #ffffff;
  --pp-surface: #ffffff;
  --pp-border: #dce1ec;
  --pp-text: #0f172a;
  --pp-muted: #6b7280;
  --pp-primary: #2563eb;
  --pp-ring: rgba(37, 99, 235, .25);
  --pp-danger: #ef4444;
  --pp-danger-700: #dc2626;
}

/* Card */
.pp-card {
  position: relative;
  /* necesario para el overlay */
  background: var(--pp-bg);
  color: var(--pp-text);
  border-radius: 16px;
  box-shadow: 0 16px 40px rgba(2, 6, 23, .18);
  overflow: hidden;
}

/* Header */
.pp-header {
  position: relative;
  padding: 18px 20px 6px;
  background: linear-gradient(180deg, #ffffff, #f7f9fd);
  border-bottom: 1px solid #eef2f7;
}

.pp-title {
  margin: 10px 0 4px;
  font-size: 20px;
  font-weight: 800;
  letter-spacing: .2px;
  color: #0f172a;
}

.pp-chip {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  font-weight: 600;
  color: #0f172a;
  background: #eef2ff;
  border: 1px solid #e0e7ff;
  padding: 6px 10px;
  border-radius: 999px;
}

.pp-close {
  position: absolute;
  right: 6px;
  top: 6px;
  color: #6b7280;
}

/* Chip de IP a la derecha (sin romper layout del header) */
.pp-ipchip {
  position: absolute;
  right: 44px;
  /* deja espacio para el botón cerrar */
  top: 10px;
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  font-weight: 600;
  color: #0f172a;
  background: #eef2ff;
  border: 1px solid #e0e7ff;
  padding: 6px 10px;
  border-radius: 999px;
}

/* Body */
.pp-body {
  padding: 18px 18px 10px;
}

/* Form */
.pp-form {
  background: var(--pp-surface);
  border: 1px solid #eef2f7;
  border-radius: 14px;
  padding: 16px;
}

/* Monto */
.pp-amount {
  border: 1.5px solid var(--pp-primary);
  border-radius: 14px;
  padding: 12px;
  margin-bottom: 14px;
  box-shadow: 0 0 0 4px var(--pp-ring);
  background: #ffffff;
}

.pp-amount__label {
  display: block;
  margin-bottom: 8px;
  font-weight: 700;
  color: #0f172a;
  font-size: 13.5px;
}

.pp-amount__input :deep(.v-input__slot) {
  border-radius: 12px !important;
  min-height: 54px !important;
  background: #ffffff;
  border: 1px solid var(--pp-border) !important;
}

.pp-amount__input :deep(input) {
  font-size: 22px !important;
  font-weight: 800 !important;
  letter-spacing: .5px;
  color: #0f172a !important;
}

.pp-amount__input :deep(.v-label) {
  color: var(--pp-muted) !important;
}

.pp-amount__hint {
  display: block;
  margin-top: 8px;
  font-size: 12px;
  color: #6b7280;
}

/* Grid */
.pp-grid {
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 12px;
  margin-top: 6px;
}

@media (min-width: 520px) {
  .pp-grid {
    grid-template-columns: 1fr 1fr;
  }
}

.pp-input :deep(.v-input__slot) {
  border-radius: 12px !important;
  min-height: 48px !important;
  background: #ffffff !important;
  border: 1px solid var(--pp-border) !important;
}

.pp-input :deep(.v-label) {
  color: #64748b !important;
}

.pp-input :deep(.v-input--is-focused .v-input__slot) {
  box-shadow: 0 0 0 3px var(--pp-ring) !important;
  border-color: var(--pp-primary) !important;
}

.pp-input :deep(input),
.pp-input :deep(textarea) {
  color: var(--pp-text) !important;
}

/* CTA */
.pp-cta {
  margin-top: 14px;
  width: 100%;
  color: #0f172a !important;
  background: var(--pp-danger) !important;
  text-transform: none;
  font-weight: 800;
  letter-spacing: .2px;
  border-radius: 12px;
  height: 46px;
  box-shadow: 0 10px 18px rgba(239, 68, 68, .18);
}

.pp-cta:hover {
  background: var(--pp-danger-700) !important;
}

.pp-amount__hint--compact {
  display: inline-block;
  font-size: 10px;
  margin-top: 4px;
  margin-right: 8px;
  line-height: 1;
}

/* Ayuda */
.pp-help {
  margin: 10px 2px 0;
  font-size: 12px;
  color: #6b7280;
}

/* Caja del widget */
.pp-box {
  margin-top: 14px;
  background: #ffffff;
  border: 1px solid #eef2f7;
  border-radius: 14px;
  padding: 10px;
}

/* Editar */
.pp-edit {
  text-align: center;
  margin-top: 10px;
}

.pp-edit-btn {
  color: #64748b !important;
  text-transform: none;
}

/* Footer */
.pp-actions {
  padding: 10px 14px 16px;
  border-top: 1px solid #eef2f7;
}

.pp-secondary {
  color: #6b7280 !important;
  text-transform: none;
}

/* Loading */
.pp-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #6b7280;
  font-size: 13px;
  margin: 10px 0;
}

.pp-spinner {
  width: 18px;
  height: 18px;
  border-radius: 999px;
  border: 2px solid #e5e7eb;
  border-top-color: var(--pp-primary);
  animation: pp-spin 1s linear infinite;
}

@keyframes pp-spin {
  to {
    transform: rotate(360deg);
  }
}

/* Launcher */
.pp-launcher {
  padding: 0;
  min-height: auto;
  box-shadow: none;
}

.pp-launcher:hover {
  transform: scale(1.02);
}

/* Forzar fondo blanco del popup en móviles */
.pp-dialog .v-card,
.pp-dialog .v-card>* {
  background-color: #ffffff !important;
}

/* Scrim opaco (evita efecto vidrio/transparentes en iOS) */
.v-overlay__scrim {
  background-color: rgba(11, 18, 32, 0.85) !important;
}

/* Desactiva filtros que causan transparencia en Safari/iOS */
@supports (-webkit-backdrop-filter: none) or (backdrop-filter: none) {
  .v-overlay__scrim {
    -webkit-backdrop-filter: none !important;
    backdrop-filter: none !important;
  }
}

/* Asegura contraste del contenido */
.pp-card,
.pp-body,
.pp-form {
  background-color: #ffffff !important;
}

/* Si usas dark mode global, evita herencia */
.theme--dark .pp-dialog .v-card,
.theme--dark .pp-dialog .v-card>* {
  background-color: #ffffff !important;
  color: #0f172a !important;
}

/* Ajustes móviles */
@media (max-width: 360px) {
  .pp-body {
    padding: 14px;
  }

  .pp-form {
    padding: 12px;
  }

  .pp-title {
    font-size: 18px;
  }
}

/* ===== 🔒 Overlay de bloqueo ===== */
.pp-gate {
  position: absolute;
  inset: 0;
  background: rgba(11, 18, 32, 0.60);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px;
  z-index: 10;
}

.pp-gate__box {
  background: #fff;
  border: 1px solid #eef2f7;
  border-radius: 14px;
  padding: 16px 18px;
  width: 100%;
  max-width: 520px;
  box-shadow: 0 16px 40px rgba(2, 6, 23, .22);
  text-align: left;
  color: #0f172a;
  display: grid;
  grid-template-columns: auto 1fr;
  grid-gap: 6px 10px;
  align-items: center;
}

.pp-gate__title {
  grid-column: 1 / -1;
  font-weight: 800;
  font-size: 16px;
  margin-top: 2px;
}

.pp-gate__msg {
  grid-column: 1 / -1;
  font-size: 13px;
  color: #6b7280;
}

.pp-gate__actions {
  grid-column: 1 / -1;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
}

.pp-gate__btn--secondary {
  color: #6b7280 !important;
  text-transform: none;
}

.pp-gate__btn--primary {
  background: var(--pp-primary) !important;
  color: #000 !important;

  text-transform: none;
  font-weight: 700;
  border-radius: 10px;
}
</style>
